import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiBellRingingBold,
    PiEnvelopeSimpleBold,
    PiFloppyDiskBold,
    PiMagnifyingGlassBold,
    PiUserBold,
    PiUsersThreeBold,
    PiXBold,
} from 'react-icons/pi';
import { Button, Input, PageHeader, Skeleton, Switch } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';

type RuleState = {
    enabled: boolean;
    channels: string[];
    dynamic: string[];
    roles: string[];
    user_ids: number[];
    users: { id: number; name: string; email: string }[];
    extra_emails: string[];
};

type Item = {
    key: string;
    area: string;
    label: string;
    description: string;
    when: string;
    channels_supported: string[];
    dynamic_supported: string[];
    rule: RuleState;
    preview: { fixed_count: number; names: string[]; people: { name: string; via: string }[]; dynamic_labels: string[]; has_dynamic: boolean };
    updated_by: string | null;
    updated_at: string | null;
};

type Options = { roles: { value: string; label: string }[]; dynamic: { value: string; label: string }[] };

type ApiError = { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={cn(
                'rounded-full border px-2.5 py-1 text-xs transition',
                active
                    ? 'border-primary bg-primary/10 font-semibold text-primary'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:text-gray-300',
            )}
        >
            {children}
        </button>
    );
}

function UserPicker({ value, onAdd, onRemove }: { value: RuleState['users']; onAdd: (u: RuleState['users'][number]) => void; onRemove: (id: number) => void }) {
    const [term, setTerm] = useState('');
    const search = useQuery({
        queryKey: ['notification-users', term],
        enabled: term.trim().length >= 2,
        queryFn: () => api.get<{ data: RuleState['users'] }>('/settings/notifications/users', { params: { search: term } }).then((r) => r.data.data),
    });
    const ids = new Set(value.map((u) => u.id));

    return (
        <div>
            <div className="flex flex-wrap gap-1.5">
                {value.map((u) => (
                    <span key={u.id} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs dark:bg-gray-700">
                        <PiUserBold className="size-3" /> {u.name}
                        <button type="button" onClick={() => onRemove(u.id)} aria-label={`Remover ${u.name}`} className="text-gray-400 hover:text-rose-600">
                            <PiXBold className="size-3" />
                        </button>
                    </span>
                ))}
            </div>
            <div className="relative mt-1.5">
                <PiMagnifyingGlassBold className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-gray-400" />
                <input
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    placeholder="Adicionar pessoa pelo nome ou e-mail…"
                    className="h-8 w-full rounded-lg border border-gray-200 bg-white ps-8 text-xs dark:border-gray-700 dark:bg-gray-900"
                />
                {term.trim().length >= 2 && (search.data ?? []).length > 0 && (
                    <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
                        {search.data!.filter((u) => !ids.has(u.id)).map((u) => (
                            <button
                                key={u.id}
                                type="button"
                                onClick={() => {
                                    onAdd(u);
                                    setTerm('');
                                }}
                                className="block w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-800"
                            >
                                <span className="font-semibold">{u.name}</span> <span className="text-gray-400">{u.email}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function RuleCard({ item, options }: { item: Item; options: Options }) {
    const qc = useQueryClient();
    const [rule, setRule] = useState<RuleState>(item.rule);
    const [emailsText, setEmailsText] = useState(item.rule.extra_emails.join(', '));
    useEffect(() => {
        setRule(item.rule);
        setEmailsText(item.rule.extra_emails.join(', '));
    }, [item.rule]);

    const emails = useMemo(() => emailsText.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean), [emailsText]);
    const dirty = JSON.stringify({ ...rule, users: undefined, extra_emails: emails }) !== JSON.stringify({ ...item.rule, users: undefined, extra_emails: item.rule.extra_emails });
    const toggle = (field: 'channels' | 'dynamic' | 'roles', value: string) =>
        setRule((r) => ({ ...r, [field]: r[field].includes(value) ? r[field].filter((v) => v !== value) : [...r[field], value] }));

    const save = useMutation({
        mutationFn: async () =>
            (
                await api.put<{ message: string; data: Item }>(`/settings/notifications/${item.key}`, {
                    enabled: rule.enabled,
                    channels: rule.channels,
                    dynamic: rule.dynamic,
                    roles: rule.roles,
                    user_ids: rule.users.map((u) => u.id),
                    extra_emails: emails,
                })
            ).data,
        onSuccess: (res) => {
            toast.success(res.message);
            void qc.invalidateQueries({ queryKey: ['notification-settings'] });
        },
        onError: (err) => {
            const data = (err as ApiError)?.response?.data;
            toast.error((data?.errors ? Object.values(data.errors)[0]?.[0] : null) ?? data?.message ?? 'Não foi possível salvar.');
        },
    });

    const nothing = rule.enabled && rule.dynamic.length === 0 && rule.roles.length === 0 && rule.users.length === 0 && emails.length === 0;

    return (
        <article className={cn('rounded-2xl border bg-white p-4 dark:bg-gray-800', rule.enabled ? 'border-gray-200 dark:border-gray-700' : 'border-dashed border-gray-300 opacity-70 dark:border-gray-600')}>
            <header className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">{item.label}</h3>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{item.description}</p>
                    <p className="mt-0.5 text-[11px] text-gray-400">Quando: {item.when}</p>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold">
                    <span className={rule.enabled ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-500'}>{rule.enabled ? 'Ligado' : 'Desligado'}</span>
                    <Switch
                        size="sm"
                        checked={rule.enabled}
                        onChange={(e) => setRule((r) => ({ ...r, enabled: e.target.checked }))}
                        aria-label={`${item.label}: ${rule.enabled ? 'ligado' : 'desligado'}`}
                        className="shrink-0 [&_label]:my-0"
                    />
                </div>
            </header>

            {rule.enabled && (
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                    <div className="space-y-3">
                        {item.channels_supported.length > 1 && (
                            <div>
                                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Como avisar</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {item.channels_supported.map((c) => (
                                        <Chip key={c} active={rule.channels.includes(c)} onClick={() => toggle('channels', c)}>
                                            {c === 'email' ? (
                                                <>
                                                    <PiEnvelopeSimpleBold className="me-1 inline size-3.5" />E-mail
                                                </>
                                            ) : (
                                                <>
                                                    <PiBellRingingBold className="me-1 inline size-3.5" />Sininho do Hub
                                                </>
                                            )}
                                        </Chip>
                                    ))}
                                </div>
                            </div>
                        )}
                        {item.dynamic_supported.length > 0 && (
                            <div>
                                <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Pessoas envolvidas no caso</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {options.dynamic.filter((d) => item.dynamic_supported.includes(d.value)).map((d) => (
                                        <Chip key={d.value} active={rule.dynamic.includes(d.value)} onClick={() => toggle('dynamic', d.value)}>
                                            {d.label}
                                        </Chip>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div>
                            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Perfis (todos os usuários do perfil)</p>
                            <div className="flex flex-wrap items-center gap-1.5">
                                {rule.roles.map((value) => (
                                    <span key={value} className="inline-flex items-center gap-1 rounded-full border border-primary bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                                        {options.roles.find((r) => r.value === value)?.label ?? value}
                                        <button type="button" onClick={() => toggle('roles', value)} aria-label="Remover perfil" className="hover:text-rose-600">
                                            <PiXBold className="size-3" />
                                        </button>
                                    </span>
                                ))}
                                <select
                                    value=""
                                    onChange={(e) => e.target.value && toggle('roles', e.target.value)}
                                    className="h-8 rounded-full border border-dashed border-gray-300 bg-white py-0 pe-7 ps-3 text-xs leading-none text-gray-600 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300"
                                    aria-label="Adicionar perfil"
                                >
                                    <option value="">+ Adicionar perfil</option>
                                    {options.roles.filter((r) => !rule.roles.includes(r.value)).map((r) => (
                                        <option key={r.value} value={r.value}>
                                            {r.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {rule.roles.length === 0 && <p className="mt-1 text-[11px] text-gray-400">Nenhum perfil — só as pessoas marcadas ao lado.</p>}
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div>
                            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">Pessoas específicas</p>
                            <UserPicker
                                value={rule.users}
                                onAdd={(u) => setRule((r) => ({ ...r, users: [...r.users, u] }))}
                                onRemove={(id) => setRule((r) => ({ ...r, users: r.users.filter((u) => u.id !== id) }))}
                            />
                        </div>
                        <div>
                            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">E-mails extras (fora do Hub)</p>
                            <textarea
                                value={emailsText}
                                onChange={(e) => setEmailsText(e.target.value)}
                                rows={2}
                                placeholder="ex.: cd@bucklerfit.com, transportadora@parceiro.com"
                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs dark:border-gray-700 dark:bg-gray-900"
                            />
                        </div>
                        <div className="rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-600 dark:bg-gray-900/50 dark:text-gray-300">
                            <p className="flex items-center gap-1 font-semibold">
                                <PiUsersThreeBold className="size-3.5" />
                                Recebem sempre: {item.preview.fixed_count} pessoa(s)
                            </p>
                            {item.preview.people.length > 0 ? (
                                <ul className="mt-1 space-y-0.5">
                                    {item.preview.people.map((p) => (
                                        <li key={p.name}>
                                            <span className="font-medium text-gray-700 dark:text-gray-200">{p.name}</span>
                                            {p.via && <span className="text-gray-400"> · {p.via}</span>}
                                        </li>
                                    ))}
                                    {item.preview.fixed_count > item.preview.people.length && <li className="text-gray-400">…</li>}
                                </ul>
                            ) : (
                                <p className="mt-0.5 text-gray-400">Ninguém por perfil ou pelo nome.</p>
                            )}
                            {item.preview.has_dynamic && (
                                <p className="mt-1.5 border-t border-gray-200 pt-1.5 dark:border-gray-700">
                                    <span className="font-semibold">Mudam a cada caso:</span> {item.preview.dynamic_labels.join(' e ').toLowerCase()} — os nomes só existem quando o aviso acontece.
                                </p>
                            )}
                            {dirty && <p className="mt-1 text-amber-700 dark:text-amber-300">Salve para atualizar a lista.</p>}
                        </div>
                    </div>
                </div>
            )}

            <footer className="mt-4 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3 dark:border-gray-700">
                <span className="text-[11px] text-gray-400">{item.updated_by ? `Alterado por ${item.updated_by} em ${item.updated_at}` : 'Configuração padrão do Hub'}</span>
                {nothing && <span className="text-[11px] font-semibold text-rose-600">Ninguém vai receber este aviso.</span>}
                <Button size="sm" className="ms-auto" disabled={!dirty} isLoading={save.isPending} onClick={() => save.mutate()}>
                    <PiFloppyDiskBold className="me-1.5 size-4" /> Salvar
                </Button>
            </footer>
        </article>
    );
}

export default function EmailNotificationsPage() {
    const [search, setSearch] = useState('');
    const { data, isLoading, isError } = useQuery({
        queryKey: ['notification-settings'],
        queryFn: () => api.get<{ data: Item[]; options: Options }>('/settings/notifications').then((r) => r.data),
    });
    const term = search.trim().toLowerCase();
    const items = (data?.data ?? []).filter((i) => !term || `${i.label} ${i.description} ${i.area}`.toLowerCase().includes(term));
    const areas = Array.from(new Set(items.map((i) => i.area)));

    return (
        <div className="space-y-5">
            <PageHeader
                title="Notificações por e-mail"
                subtitle="Quem recebe cada aviso automático do Hub. E-mails pessoais (código de login, senha, NF da própria comissão) vão sempre só para a própria pessoa."
                breadcrumb={[{ name: 'Configurações' }, { name: 'Notificações por e-mail' }]}
            />
            <div className="relative max-w-md">
                <PiMagnifyingGlassBold className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar aviso…" className="ps-9" />
            </div>
            {isLoading ? (
                <div className="space-y-3">
                    {[0, 1, 2].map((i) => (
                        <Skeleton key={i} className="h-40 rounded-2xl" />
                    ))}
                </div>
            ) : isError ? (
                <p className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-6 text-center text-sm text-rose-700">Não foi possível carregar as notificações.</p>
            ) : (
                areas.map((area) => (
                    <section key={area} className="space-y-3">
                        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">{area}</h2>
                        {items.filter((i) => i.area === area).map((item) => (
                            <RuleCard key={item.key} item={item} options={data!.options} />
                        ))}
                    </section>
                ))
            )}
        </div>
    );
}
